/**
 * The dunning queue (redesign phase 4d, catalogue C1).
 *
 * ── Why this is not a report ────────────────────────────────────────────
 *
 * Every row here is a customer who is, right now, being refused AI. `past_due`
 * is checked ahead of every other branch in `cost-check`; an open dispute
 * refuses with `COST_LIMIT_DISPUTED`. Both were already enforced and neither
 * appeared on any screen, so a paying customer could be hard-blocked while
 * nobody on this side had a page that said so — the operator found out from
 * Stripe's email and then went looking for the tenant by name.
 *
 * ── What this screen deliberately does NOT do ───────────────────────────
 *
 * It does not downgrade anyone automatically. Stripe already cancels after its
 * own retry schedule, the webhook mirror handles `subscription.deleted`, and
 * `applyEntitlements` reverts to free. A second automatic path on our timer
 * would race that and turn a transient card decline into an outage we caused.
 * The manual "downgrade now" lever lands here in phase 7 — on this row, because
 * the queue and the lever are one workflow.
 *
 * Invoice actions (void, refund, resend) are not here either: Stripe is
 * authoritative, so we deep-link to its invoice page rather than reimplementing
 * a payments console.
 *
 * Empty is the GOOD state and says so — an empty region is indistinguishable
 * from a failed load, the same shape `StuckWebhooksPanel` already takes.
 */
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, CheckCircle2, ExternalLink } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { formatCurrencyMinor, formatPlatformDateLocal } from '@papan/types'
import { dunningAdminApi, type DunningRow } from '../client/dunning-admin.js'
import { NUMERIC } from './styles.js'

// Platform-shelf panel → Malaysia date order (en-MY), LOCAL time: `dueAt` and
// `disputedAt` are Stripe wall-clock instants, not midnight-UTC civil days.
const fmtDate = (iso: string | null) => formatPlatformDateLocal(iso)

/** Whole days from now until `iso`, floored. Negative means already past. */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  return Math.floor((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

export function DunningPanel({ onOpenCustomer }: {
  /** The queue answers "who", the customer page answers "and then what" — so
   *  every row is a link into it rather than a dead label. */
  onOpenCustomer?: (tenantId: string) => void
}) {
  const { t } = useTranslation('billing')
  const [rows, setRows] = useState<DunningRow[]>([])
  const [loading, setLoading] = useState(true)
  /** Billing off ⇒ the route 404s. That is a different statement from "nobody
   *  is past due" and must not render as one. */
  const [unavailable, setUnavailable] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await dunningAdminApi.list()
      setRows(r.subscriptions)
      setUnavailable(false)
    } catch {
      setUnavailable(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h2 className="themed-text text-lg font-medium flex items-center gap-2">
          {t('dunningQueue.title', { defaultValue: 'Payment problems' })}
          {rows.length > 0 && (
            <span data-toc-ignore className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-medium tabular-nums">
              {rows.length}
            </span>
          )}
        </h2>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="text-xs themed-text-muted hover:themed-text flex items-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-default"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : undefined} />
          {t('webhooks.refresh')}
        </button>
      </div>
      {/* The explanation renders only when there is something to explain.
          ────────────────────────────────────────────────────────────────
          On a manual-billing deployment this queue is STRUCTURALLY empty —
          `plan_authority` decides which of the two unpaid queues can have rows,
          and only one ever does — so the heading plus a two-line paragraph plus
          "no tenant has a payment problem" was five lines of permanent furniture
          above the queues that do have rows. The empty state below still says
          the queue was read and was clear, which is the part that must not be
          dropped: "nothing is wrong" and "we did not look" have to stay
          distinguishable. */}
      {(unavailable || rows.length > 0) && (
        <p className="themed-text-muted text-sm mb-4">
          {t('dunningQueue.subtitle', {
            defaultValue:
              'Tenants whose payment has failed, is disputed, or is inside its grace window — already being refused AI where past-due or disputed. Nothing to act on here: the provider keeps retrying, and a row clears itself once it collects or the customer disputes.',
          })}
        </p>
      )}

      {unavailable && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <p className="text-amber-300 text-xs font-medium">
            {t('dunningQueue.unavailable', { defaultValue: 'The dunning queue could not be read.' })}
          </p>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('dunningQueue.unavailableBody', {
              defaultValue:
                'Billing is switched off on this deployment, or the request failed. This is not a claim that nobody is past due.',
            })}
          </p>
        </div>
      )}

      {!unavailable && !loading && rows.length === 0 && (
        <div className="flex items-center gap-2 themed-text-secondary text-sm">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
          <span>{t('dunningQueue.empty', { defaultValue: 'No tenant has a payment problem.' })}</span>
        </div>
      )}

      {!unavailable && rows.length > 0 && (
        <div className="space-y-2" data-testid="dunning-list">
          {rows.map((r) => <DunningCard key={r.id} row={r} onOpenCustomer={onOpenCustomer} />)}
        </div>
      )}
    </div>
  )
}

function DunningCard({ row, onOpenCustomer }: {
  row: DunningRow
  onOpenCustomer?: (tenantId: string) => void
}) {
  const { t } = useTranslation('billing')
  const graceDays = daysUntil(row.graceUntil)
  // A dispute outranks past-due, which outranks a grace window — the same
  // precedence `cost-check` enforces and the customer health badge shows, so
  // the two surfaces cannot disagree about how bad a situation is.
  const state = row.disputedAt ? 'disputed' : row.status === 'past_due' ? 'pastDue' : 'inGrace'
  const tone = state === 'inGrace' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'

  return (
    <div className="themed-bg-secondary rounded-lg p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {onOpenCustomer ? (
              <button
                onClick={() => onOpenCustomer(row.tenantId)}
                className="themed-text text-sm font-medium truncate hover:underline"
              >
                {row.tenantName ?? row.tenantId}
              </button>
            ) : (
              <span className="themed-text text-sm font-medium truncate">{row.tenantName ?? row.tenantId}</span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${tone}`}>
              {state === 'disputed'
                ? t('dunningQueue.state.disputed', { defaultValue: 'Payment disputed' })
                : state === 'pastDue'
                  ? t('dunningQueue.state.pastDue', { defaultValue: 'Past due' })
                  : t('dunningQueue.state.inGrace', { defaultValue: 'In grace' })}
            </span>
          </div>
          <p className={`themed-text-muted text-xs mt-0.5 truncate ${NUMERIC}`}>
            {row.planName}
            {row.seatCount != null && ` · ${t('dunningQueue.seats', { defaultValue: '{{n}} seats', n: row.seatCount })}`}
          </p>
        </div>

        {row.invoice?.hostedInvoiceUrl && (
          // Stripe is authoritative for invoice actions; we link rather than
          // rebuild a payments console (an explicit non-goal of the redesign).
          <a
            href={row.invoice.hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="themed-btn-secondary text-xs rounded px-2 py-1 shrink-0 flex items-center gap-1"
          >
            {t('dunningQueue.openInvoice', { defaultValue: 'Invoice' })}
            <ExternalLink size={11} />
          </a>
        )}
      </div>

      <div className={`flex flex-wrap gap-x-4 gap-y-0.5 mt-2 text-xs themed-text-muted ${NUMERIC}`}>
        {row.invoice && (
          <>
            <span>
              {t('dunningQueue.amount', {
                defaultValue: 'Outstanding {{amount}}',
                amount: formatCurrencyMinor(row.invoice.totalMinor, row.invoice.currency),
              })}
            </span>
            <span>{t('dunningQueue.attempts', { defaultValue: '{{n}} collection attempts', n: row.invoice.attemptCount })}</span>
            <span>{t('dunningQueue.dueAt', { defaultValue: 'Due {{at}}', at: fmtDate(row.invoice.dueAt) })}</span>
          </>
        )}
        {row.disputedAt && (
          <span>{t('dunningQueue.disputedAt', { defaultValue: 'Disputed {{at}}', at: fmtDate(row.disputedAt) })}</span>
        )}
        {graceDays != null && (
          <span className={graceDays <= 1 ? 'text-amber-300' : undefined}>
            {/* Days, not a timestamp: the question is "how long have I got", and
                nobody wants to subtract two dates to find out. */}
            {graceDays <= 0
              ? t('dunningQueue.graceToday', { defaultValue: 'Grace ends today' })
              : t('dunningQueue.graceDays', { defaultValue: 'Grace ends in {{n}} days', n: graceDays })}
          </span>
        )}
      </div>

      {/* What actually happens next, in words. The mechanism is Stripe's retry
          schedule plus our mirror; leaving the operator to infer that is how a
          "why did nothing happen" ticket gets written. */}
      <p className="themed-text-secondary text-xs mt-2">
        {state === 'disputed'
          ? t('dunningQueue.nextDisputed', {
              defaultValue:
                'AI is refused until the dispute closes. Resolving it with the payment provider clears this automatically — a won dispute is the only thing that lifts it.',
            })
          : t('dunningQueue.nextPastDue', {
              defaultValue:
                'The payment provider keeps retrying on its own schedule. If it gives up, the subscription is cancelled and this tenant drops to the free plan automatically.',
            })}
      </p>
    </div>
  )
}
